import type { ServiceCode, ServiceType } from '@common/enums';
import type { IService } from '@common/interfaces';

export type ServiceWithCode = { service: Pick<IService, 'code'> };

type ServiceTypeAndCode = Pick<IService, 'code' | 'serviceType'>;

type NestedOptionalServiceType = { service?: Partial<IService> | Pick<IService, 'serviceType'> };

type NestedServiceTypeAndCode = { service?: ServiceTypeAndCode };

const serviceCodeSortOrder: { [key in ServiceCode]: number } = {
    BariPost: 1,
    BariPre: 0,
    DermaC: 1,
    DermaCsc: 3,
    DermaCwond: 2,
    DermaOth: 0,
    DermaSnelC: 4,
    DermaSnelCsc: 5,
    Echography: 0,
    GGZBmind: 2,
    GGZBther: 3,
    GGZBtherTreat: 9,
    GGZCf2f: 7,
    GGZCg: 4,
    GGZCmdo: 8,
    GGZCt: 5,
    GGZCtriage: 6,
    GGZOembl: 0,
    GGZOint: 1,
    GGZv: 10,
    GTC: 1,
    GTO: 0,
    Headache: 0,
    HearingLoss: 0,
    Holter: 0,
    LAB: 0,
    LumbarRadiculopathy: 0,
    OCRK: 9,
    OORK: 8,
    OSAS: 0,
    OSASC1: 1,
    OSASC2: 3,
    OSASE: 6,
    OSASEND: 7,
    OSASN: 9,
    OSASO: 2,
    OSASQ: 8,
    OSASV: 0,
    OSASVC: 5,
    OSASVO: 4,
    OrthC: 5,
    OrthO: 4,
    OrthOFol: 6,
    PILOT: 9999,
    RespiAller: 8888,
    TCCh: 1,
    TCCi: 3,
    TCCr: 5,
    TCOH: 0,
    TCOR: 4,
    TFC: 7,
    TFO: 2,
    TIECG: 2,
    TOC: 1,
    TOO: 0,
    TOV: 3,
    TPC: 1,
    TPCFlex: 1,
    TPO: 0,
    TPOFlex: 0,
};

export const serviceTypeSortOrder: { [key in ServiceType]: number } = {
    consultation: 3,
    contact: 6,
    counseling: 2,
    examination: 1,
    inquiry: 7,
    invitation: 8,
    other: 9,
    referral: 4,
    referralSmall: 5,
};

export const serviceTypeButtonSortOrder: { [key in ServiceType]: number } = {
    consultation: 5,
    contact: 1,
    counseling: 4,
    examination: 3,
    inquiry: 2,
    invitation: 9,
    other: 9,
    referral: 6,
    referralSmall: 7,
};

export function compareByNestedServiceTypeSortOrderAndCode(a: NestedServiceTypeAndCode, b: NestedServiceTypeAndCode) {
    return compareByServiceTypeSortOrderAndCode(a.service, b.service);
}

export function compareByServiceTypeSortOrderAndCode(a?: ServiceTypeAndCode, b?: ServiceTypeAndCode) {
    const serviceTypeEquality = compareByServiceTypeSortOrder(a?.serviceType, b?.serviceType);

    if (serviceTypeEquality === 0 && a && b) {
        return a.code.localeCompare(b.code);
    }

    return serviceTypeEquality;
}

export function compareByNestedServiceTypeSortOrder(a: NestedOptionalServiceType, b: NestedOptionalServiceType) {
    return compareByServiceTypeSortOrder(a.service?.serviceType, b.service?.serviceType);
}

function compareByServiceTypeSortOrder(a?: ServiceType, b?: ServiceType) {
    if (!a && !b) {
        return 0;
    }
    if (!a || !b) {
        return !a ? 1 : -1;
    }

    return serviceTypeSortOrder[a] - serviceTypeSortOrder[b];
}

export function compareByServiceCodeSortOrder(a: ServiceWithCode, b: ServiceWithCode) {
    return serviceCodeSortOrder[a.service.code] - serviceCodeSortOrder[b.service.code];
}

export function compareByServiceTypeButtonSortOrder(a: ServiceType, b: ServiceType) {
    return serviceTypeButtonSortOrder[a] - serviceTypeButtonSortOrder[b];
}
